import { GlBuffer, InteractionEvent, CanvasPoolSingleton, ImageResource, Padding, Matrix, Point, Canvas, Loader, Target } from '@tencent/alloy-renderer';
import DOMPurify from 'dompurify';

var WEBGL_OPTIONS = {
  preserveDrawingBuffer: true
};
var BrowserGlBuffer = /*@__PURE__*/function (GlBuffer) {
  function BrowserGlBuffer(width, height) {
    GlBuffer.call(this);
    this._width = width;
    this._height = height;
    var canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    this.native = canvas;
    var gl = canvas.getContext("webgl", WEBGL_OPTIONS) || canvas.getContext("experimental-webgl", WEBGL_OPTIONS);

    if (!gl) {
      throw new TypeError("WebGL is not supported.");
    }

    this.gl = gl;
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  }

  if (GlBuffer) BrowserGlBuffer.__proto__ = GlBuffer;
  BrowserGlBuffer.prototype = /*#__PURE__*/Object.create(GlBuffer && GlBuffer.prototype);
  BrowserGlBuffer.prototype.constructor = BrowserGlBuffer;
  var prototypeAccessors = {
    width: {
      configurable: true
    },
    height: {
      configurable: true
    }
  };

  BrowserGlBuffer.getInstance = function getInstance() {
    if (!BrowserGlBuffer.instance) {
      BrowserGlBuffer.instance = new BrowserGlBuffer(1, 1);
    }

    return BrowserGlBuffer.instance;
  };

  prototypeAccessors.width.get = function width() {
    return this._width;
  };

  prototypeAccessors.height.get = function height() {
    return this._height;
  };

  BrowserGlBuffer.prototype.resize = function resize(width, height) {
    this._width = width;
    this._height = height;
    this.native.width = width;
    this.native.height = height;
  };

  Object.defineProperties(BrowserGlBuffer.prototype, prototypeAccessors);
  return BrowserGlBuffer;
}(GlBuffer);

var DomInteractionEvent = /*@__PURE__*/function (InteractionEvent) {
  function DomInteractionEvent(options) {
    var type = options.type ? nativeToInteractionMap[options.type] : nativeToInteractionMap[options.nativeEvent.type];

    if (!type) {
      throw new TypeError("Native event " + options.nativeEvent.type + " is not supported in alloy-renderer.");
    }

    InteractionEvent.call(this, Object.assign({}, options, {
      type: type
    }));
  }

  if (InteractionEvent) DomInteractionEvent.__proto__ = InteractionEvent;
  DomInteractionEvent.prototype = /*#__PURE__*/Object.create(InteractionEvent && InteractionEvent.prototype);
  DomInteractionEvent.prototype.constructor = DomInteractionEvent;

  DomInteractionEvent.prototype.stopPropagation = function stopPropagation() {
    if (this.type === nativeToInteractionMap[this.nativeEvent.type]) {
      this.nativeEvent.stopPropagation();
    }

    InteractionEvent.prototype.stopPropagation.call(this);
  };

  DomInteractionEvent.prototype.preventDefault = function preventDefault() {
    if (this.type === nativeToInteractionMap[this.nativeEvent.type]) {
      this.nativeEvent.preventDefault();
    }
  };

  return DomInteractionEvent;
}(InteractionEvent);
var nativeToInteractionMap = {
  touchstart: "onTouchStart",
  touchcancel: "onTouchCancel",
  touchend: "onTouchEnd",
  touchmove: "onTouchMove",
  mousemove: "onMouseMove",
  mousedown: "onMouseDown",
  mouseleave: "onMouseLeave",
  mouseenter: "onMouseEnter",
  mouseup: "onMouseUp",
  click: "onClick",
  dblclick: "onDoubleClick",
  dragover: "onDragOver",
  drop: "onDrop",
  wheel: "onWheel",
  contextmenu: "onContextMenu"
};

var BrowserCanvas = /*@__PURE__*/function (Canvas) {
  function BrowserCanvas(options) {
    var this$1$1 = this;
    if (options === void 0) options = {};
    Canvas.call(this);
    this._width = 0;
    this._height = 0;
    this._resolution = 1;
    this.supportsTouchEvents = "ontouchstart" in window;

    this.onNativeEvent = function (nativeEvent) {
      if (!this$1$1.onEvents) {
        return;
      }

      var syntheticEvents = this$1$1.synthesizeNativeEvent(nativeEvent);
      this$1$1.onEvents(syntheticEvents);
    };

    this.native = options.native || document.createElement("canvas");
    this._resolution = options.resolution || 1;
    this.ctx = this.native.getContext("2d");
    this._state = new BrowserCanvasState(this.ctx, this._transform);
    this.useGlobalGlBuffer = options.useGlobalGlBuffer !== false;
    this.modifyNativeStyle = options.modifyNativeStyle !== false;

    if (options.bindEvents !== false) {
      this.bindNativeEvents();
    }
  }

  if (Canvas) BrowserCanvas.__proto__ = Canvas;
  BrowserCanvas.prototype = /*#__PURE__*/Object.create(Canvas && Canvas.prototype);
  BrowserCanvas.prototype.constructor = BrowserCanvas;
  var prototypeAccessors = {
    resolution: {
      configurable: true
    },
    width: {
      configurable: true
    },
    height: {
      configurable: true
    },
    cursor: {
      configurable: true
    },
    autoMount: {
      configurable: true
    },
    font: {
      configurable: true
    },
    filter: {
      configurable: true
    },
    globalAlpha: {
      configurable: true
    },
    globalCompositeOperation: {
      configurable: true
    },
    textBaseline: {
      configurable: true
    },
    nativeFillStyle: {
      configurable: true
    },
    nativeStrokeStyle: {
      configurable: true
    },
    lineWidth: {
      configurable: true
    },
    lineCap: {
      configurable: true
    },
    lineJoin: {
      configurable: true
    },
    lineDash: {
      configurable: true
    },
    lineDashOffset: {
      configurable: true
    },
    miterLimit: {
      configurable: true
    }
  };

  prototypeAccessors.resolution.get = function resolution() {
    return this._resolution;
  };

  prototypeAccessors.resolution.set = function resolution(newResolution) {
    var ref = this;
    var width = ref.width;
    var height = ref.height;
    this._resolution = newResolution;
    this.width = width;
    this.height = height;
  };

  prototypeAccessors.width.get = function width() {
    return this._width;
  };

  prototypeAccessors.width.set = function width(value) {
    var newValue = Math.ceil(value);
    this._width = newValue;

    if (this.modifyNativeStyle) {
      var newWidth = Math.ceil(value * this.resolution);

      if (this.native.width !== newWidth) {
        this.native.width = newWidth;
      }

      this.native.style.width = newValue + "px";

      this._state.forceUpdate();
    }
  };

  prototypeAccessors.height.get = function height() {
    return this._height;
  };

  prototypeAccessors.height.set = function height(value) {
    var newValue = Math.ceil(value);
    this._height = newValue;

    if (this.modifyNativeStyle) {
      var newHeight = Math.ceil(value * this.resolution);

      if (this.native.height !== newHeight) {
        this.native.height = newHeight;
      }

      this.native.style.height = newValue + "px";

      this._state.forceUpdate();
    }
  };

  prototypeAccessors.cursor.get = function cursor() {
    return this.native.style.cursor;
  };

  prototypeAccessors.cursor.set = function cursor(value) {
    this.native.style.cursor = value;
  };

  prototypeAccessors.autoMount.get = function autoMount() {
    return this._autoMount;
  };

  prototypeAccessors.autoMount.set = function autoMount(value) {
    if (this._autoMount === value) {
      return;
    }

    if (!this._autoMount && !this.native.parentNode) {
      this.native.style.display = "none";
      document.body.appendChild(this.native);
    } else if (this._autoMount && this.native.parentNode) {
      this.native.parentNode.removeChild(this.native);
    }

    this._autoMount = value;
  };

  prototypeAccessors.font.get = function font() {
    return this._state.font;
  };

  prototypeAccessors.font.set = function font(value) {
    this._state.set("font", value);
  };

  prototypeAccessors.filter.get = function filter() {
    return this._state.filter;
  };

  prototypeAccessors.filter.set = function filter(value) {
    this._state.set("filter", value);
  };

  prototypeAccessors.globalAlpha.get = function globalAlpha() {
    return this._state.globalAlpha;
  };

  prototypeAccessors.globalAlpha.set = function globalAlpha(value) {
    this._state.set("globalAlpha", value);
  };

  prototypeAccessors.globalCompositeOperation.get = function globalCompositeOperation() {
    return this._state.globalCompositeOperation;
  };

  prototypeAccessors.globalCompositeOperation.set = function globalCompositeOperation(value) {
    this._state.set("globalCompositeOperation", value);
  };

  prototypeAccessors.textBaseline.get = function textBaseline() {
    return this._state.textBaseline;
  };

  prototypeAccessors.textBaseline.set = function textBaseline(value) {
    this._state.set("textBaseline", value);
  };

  prototypeAccessors.nativeFillStyle.get = function nativeFillStyle() {
    return this._state.fillStyle;
  };

  prototypeAccessors.nativeFillStyle.set = function nativeFillStyle(value) {
    this._state.set("fillStyle", value);
  };

  prototypeAccessors.nativeStrokeStyle.get = function nativeStrokeStyle() {
    return this._state.strokeStyle;
  };

  prototypeAccessors.nativeStrokeStyle.set = function nativeStrokeStyle(value) {
    this._state.set("strokeStyle", value);
  };

  prototypeAccessors.lineWidth.get = function lineWidth() {
    return this._state.lineWidth;
  };

  prototypeAccessors.lineWidth.set = function lineWidth(value) {
    this._state.set("lineWidth", value);
  };

  prototypeAccessors.lineCap.get = function lineCap() {
    return this._state.lineCap;
  };

  prototypeAccessors.lineCap.set = function lineCap(value) {
    this._state.set("lineCap", value);
  };

  prototypeAccessors.lineJoin.get = function lineJoin() {
    return this._state.lineJoin;
  };

  prototypeAccessors.lineJoin.set = function lineJoin(value) {
    this._state.set("lineJoin", value);
  };

  prototypeAccessors.lineDash.get = function lineDash() {
    return this._state.lineDash;
  };

  prototypeAccessors.lineDash.set = function lineDash(value) {
    this._state.set("lineDash", value);
  };

  prototypeAccessors.lineDashOffset.get = function lineDashOffset() {
    return this._state.lineDashOffset;
  };

  prototypeAccessors.lineDashOffset.set = function lineDashOffset(value) {
    this._state.set("lineDashOffset", value);
  };

  prototypeAccessors.miterLimit.get = function miterLimit() {
    return this._state.miterLimit;
  };

  prototypeAccessors.miterLimit.set = function miterLimit(value) {
    this._state.set("miterLimit", value);
  };

  BrowserCanvas.prototype.createPattern = function createPattern(image, repetition) {
    if (image instanceof Canvas || image instanceof GlBuffer) {
      return this.ctx.createPattern(image.native, repetition);
    }

    if (image instanceof ImageResource) {
      return this.ctx.createPattern(image.data, repetition);
    }

    return this.ctx.createPattern(image, repetition);
  };

  BrowserCanvas.prototype.createLinearGradient = function createLinearGradient(x0, y0, x1, y1) {
    return this.ctx.createLinearGradient(x0, y0, x1, y1);
  };

  BrowserCanvas.prototype.createRadialGradient = function createRadialGradient(x0, y0, r0, x1, y1, r1) {
    return this.ctx.createRadialGradient(x0, y0, r0, x1, y1, r1);
  };

  BrowserCanvas.prototype.drawCanvas = function drawCanvas(canvas, x, y, dw, dh, sx, sy, sw, sh) {
    if (x === void 0) x = 0;
    if (y === void 0) y = 0;
    if (sx === void 0) sx = 0;
    if (sy === void 0) sy = 0;
    this.commit();
    var padding = canvas instanceof GlBuffer ? Padding.Zero : canvas.padding;
    var scaleX = (canvas instanceof Canvas ? canvas.contextScale.sx : 1) / this.contextScale.sx;
    var scaleY = (canvas instanceof Canvas ? canvas.contextScale.sy : 1) / this.contextScale.sy;
    var dx = x - padding.left / scaleX;
    var dy = y - padding.top / scaleY;
    var width = dw !== void 0 ? dw : canvas.width;
    var height = dh !== void 0 ? dh : canvas.height;

    if (sw !== void 0 && sh !== void 0) {
      this.ctx.drawImage(canvas.native, sx, sy, sw, sh, dx, dy, width, height);
    } else {
      this.ctx.drawImage(canvas.native, dx, dy, width, height);
    }
  };

  BrowserCanvas.prototype.drawImage = function drawImage(image, dx, dy, dw, dh, sx, sy, sw, sh) {
    if (sx === void 0) sx = 0;
    if (sy === void 0) sy = 0;
    this.commit();
    var data = image.data;

    if (sw !== void 0 && sh !== void 0) {
      this.ctx.drawImage(data, sx, sy, sw, sh, dx, dy, dw, dh);
    } else {
      if (dw !== void 0 && dh !== void 0) {
        this.ctx.drawImage(data, dx, dy, dw, dh);
      } else {
        this.ctx.drawImage(data, dx, dy);
      }
    }
  };

  BrowserCanvas.prototype.clip = function clip() {
    this.ctx.clip();
  };

  BrowserCanvas.prototype.fill = function fill(fillRule) {
    if (fillRule === void 0) fillRule = "evenodd";
    this.commit();
    this.ctx.fill(fillRule);
  };

  BrowserCanvas.prototype.stroke = function stroke() {
    this.commit();
    this.ctx.stroke();
  };

  BrowserCanvas.prototype.beginPath = function beginPath() {
    this.ctx.beginPath();
  };

  BrowserCanvas.prototype.closePath = function closePath() {
    this.ctx.closePath();
  };

  BrowserCanvas.prototype.arc = function arc(x, y, radius, startAngle, endAngle, counterclockwise) {
    this.commit();
    this.ctx.arc(x, y, radius, startAngle, endAngle, counterclockwise);
  };

  BrowserCanvas.prototype.arcTo = function arcTo(x1, y1, x2, y2, radius) {
    this.commit();
    this.ctx.arcTo(x1, y2, x2, y2, radius);
  };

  BrowserCanvas.prototype.bezierCurveTo = function bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y) {
    this.commit();
    this.ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y);
  };

  BrowserCanvas.prototype.ellipse = function ellipse(x, y, radiusX, radiusY, rotation, startAngle, endAngle, counterclockwise) {
    this.commit();
    this.ctx.ellipse(x, y, radiusX, radiusY, rotation, startAngle, endAngle, counterclockwise);
  };

  BrowserCanvas.prototype.fillRect = function fillRect(x, y, width, height) {
    this.commit();
    this.ctx.fillRect(x, y, width, height);
  };

  BrowserCanvas.prototype.lineTo = function lineTo(x, y) {
    this.commit();
    this.ctx.lineTo(x, y);
  };

  BrowserCanvas.prototype.moveTo = function moveTo(x, y) {
    this.commit();
    this.ctx.moveTo(x, y);
  };

  BrowserCanvas.prototype.quadraticCurveTo = function quadraticCurveTo(cpx, cpy, x, y) {
    this.commit();
    this.ctx.quadraticCurveTo(cpx, cpy, x, y);
  };

  BrowserCanvas.prototype.rect = function rect(x, y, w, h) {
    this.commit();
    this.ctx.rect(x, y, w, h);
  };

  BrowserCanvas.prototype.clear = function clear() {
    Canvas.prototype.clear.call(this);
    this.resetContext();
  };

  BrowserCanvas.prototype.clearRect = function clearRect(x, y, w, h) {
    this.commit();
    this.ctx.clearRect(x, y, w, h);
  };

  BrowserCanvas.prototype.fillText = function fillText(text, x, y) {
    this.commit();
    this.ctx.fillText(text, x, y);
  };

  BrowserCanvas.prototype.strokeText = function strokeText(text, x, y) {
    this.commit();
    this.ctx.strokeText(text, x, y);
  };

  BrowserCanvas.prototype.measureText = function measureText(text) {
    this.commit();
    return this.ctx.measureText(text);
  };

  BrowserCanvas.prototype.getImageData = function getImageData(x, y, w, h) {
    return this.ctx.getImageData(x, y, w, h);
  };

  BrowserCanvas.prototype.putImageData = function putImageData(imagedata, dx, dy) {
    this.ctx.putImageData(imagedata, dx, dy);
  };

  BrowserCanvas.prototype.toDataUrl = function toDataUrl(type, quality) {
    return this.native.toDataURL(type, quality);
  };

  BrowserCanvas.prototype.toBlob = function toBlob(callback, type, quality) {
    this.native.toBlob(callback, type, quality);
  };

  BrowserCanvas.prototype.save = function save() {
    this._state.save();

    this.ctx.save();
  };

  BrowserCanvas.prototype.restore = function restore() {
    this._state.restore();

    this.ctx.restore();
  };

  BrowserCanvas.prototype.pad = function pad(padding) {
    if (padding.top === 0 && padding.right === 0 && padding.bottom === 0 && padding.left === 0) {
      return;
    }

    var buffer = CanvasPoolSingleton.getInstance().checkOut(this.constructor);
    buffer.resetBufferInfo(this, this.width, this.height, this.resolution, true);
    buffer.padding.copyFrom(this.padding);
    buffer.transform = Matrix.Identity;
    buffer.drawCanvas(this);
    tempPadding.copyFrom(padding).expand();
    this.padding.add(tempPadding);
    this.transform = this.transform;
    this.save();
    this.width += tempPadding.left + tempPadding.right;
    this.height += tempPadding.top + tempPadding.bottom;
    this.transform = new Matrix(1, 0, 0, 1, -this.contextTranslate.x, -this.contextTranslate.y);
    this.drawCanvas(buffer, 0, 0);
    this.restore();
    CanvasPoolSingleton.getInstance().checkIn(buffer);
  };

  BrowserCanvas.prototype.createBuffer = function createBuffer(width, height, resolution) {
    if (width === void 0) width = this.width;
    if (height === void 0) height = this.height;
    if (resolution === void 0) resolution = this.resolution;
    var buffer = new BrowserCanvas(BUFFER_OPTIONS);
    buffer.isBuffer = true;
    buffer.parent = this;
    buffer.resolution = resolution;
    buffer.width = width;
    buffer.height = height;
    buffer.transform = Matrix.Identity;
    return buffer;
  };

  BrowserCanvas.prototype.resetBufferInfo = function resetBufferInfo(parent, width, height, resolution, inheritScale) {
    this.isBuffer = true;

    if (parent) {
      this.parent = parent;
    }

    if (resolution) {
      this.resolution = resolution;
    }

    if (width) {
      this.width = width;
    }

    if (height) {
      this.height = height;
    }

    if (inheritScale && parent) {
      this.contextScale.sx = parent.contextScale.sx;
      this.contextScale.sy = parent.contextScale.sy;
    }

    this.transform = Matrix.Identity;
  };

  BrowserCanvas.prototype.resetContext = function resetContext() {
    this._state.resetStates(this.ctx);
  };

  BrowserCanvas.prototype.dispose = function dispose() {
    if (this.supportsTouchEvents) {
      this.native.removeEventListener("touchstart", this.onNativeEvent);
      this.native.removeEventListener("touchcancel", this.onNativeEvent);
      this.native.removeEventListener("touchend", this.onNativeEvent);
      this.native.removeEventListener("touchmove", this.onNativeEvent);
    }

    this.native.removeEventListener("mousemove", this.onNativeEvent);
    this.native.removeEventListener("mouseup", this.onNativeEvent);
    this.native.removeEventListener("mousedown", this.onNativeEvent);
    this.native.removeEventListener("mouseleave", this.onNativeEvent);
    this.native.removeEventListener("mouseenter", this.onNativeEvent);
    this.native.removeEventListener("contextmenu", this.onNativeEvent);
    this.native.removeEventListener("click", this.onNativeEvent);
    this.native.removeEventListener("dblclick", this.onNativeEvent);
    this.native.removeEventListener("dragover", this.onNativeEvent);
    this.native.removeEventListener("drop", this.onNativeEvent);
    this.native.removeEventListener("wheel", this.onNativeEvent, {
      capture: true
    });
  };

  BrowserCanvas.prototype.setTransform = function setTransform(a, b, c, d, tx, ty) {
    this._state.setTransform(a, b, c, d, tx, ty);
  };

  BrowserCanvas.prototype.createGlBuffer = function createGlBuffer(width, height, resolution) {
    if (resolution === void 0) resolution = this.resolution;

    try {
      var w = (width !== void 0 ? width : this.width) * resolution;
      var h = (height !== void 0 ? height : this.height) * resolution;

      if (this.useGlobalGlBuffer) {
        var buffer = BrowserGlBuffer.getInstance();

        if (width !== void 0 && height !== void 0) {
          buffer.resize(w, h);
        }

        return buffer;
      }

      return new BrowserGlBuffer(w, h);
    } catch (e) {
      return null;
    }
  };

  BrowserCanvas.prototype.commit = function commit() {
    this._state.commit(this.ctx);
  };

  BrowserCanvas.prototype.canUseTouchEvent = function canUseTouchEvent(e, supportsTouchEvents) {
    return supportsTouchEvents && e instanceof TouchEvent;
  };

  BrowserCanvas.prototype.synthesizeNativeEvent = function synthesizeNativeEvent(nativeEvent) {
    var syntheticEvent = [];

    if (this.canUseTouchEvent(nativeEvent, this.supportsTouchEvents)) {
      for (var i = 0, list = nativeEvent.changedTouches; i < list.length; i += 1) {
        var touch = list[i];
        var worldPoint = this.getWorldPosition(touch.clientX, touch.clientY);

        if (!worldPoint) {
          return [];
        }

        var eventOption = {
          nativeEvent: nativeEvent,
          worldX: worldPoint.x,
          worldY: worldPoint.y
        };
        syntheticEvent.push(new DomInteractionEvent(eventOption));
      }
    } else {
      var worldPoint$1 = this.getWorldPosition(nativeEvent.clientX, nativeEvent.clientY);

      if (!worldPoint$1) {
        return [];
      }

      var eventOption$1 = {
        nativeEvent: nativeEvent,
        worldX: worldPoint$1.x,
        worldY: worldPoint$1.y
      };
      syntheticEvent.push(new DomInteractionEvent(eventOption$1));
    }

    return syntheticEvent;
  };

  BrowserCanvas.prototype.bindNativeEvents = function bindNativeEvents() {
    if (this.supportsTouchEvents) {
      this.native.addEventListener("touchstart", this.onNativeEvent);
      this.native.addEventListener("touchcancel", this.onNativeEvent);
      this.native.addEventListener("touchend", this.onNativeEvent);
      this.native.addEventListener("touchmove", this.onNativeEvent);
    }

    this.native.addEventListener("mousemove", this.onNativeEvent);
    this.native.addEventListener("mouseup", this.onNativeEvent);
    this.native.addEventListener("mousedown", this.onNativeEvent);
    this.native.addEventListener("mouseleave", this.onNativeEvent);
    this.native.addEventListener("mouseenter", this.onNativeEvent);
    this.native.addEventListener("contextmenu", this.onNativeEvent);
    this.native.addEventListener("click", this.onNativeEvent);
    this.native.addEventListener("dblclick", this.onNativeEvent);
    this.native.addEventListener("dragover", this.onNativeEvent);
    this.native.addEventListener("drop", this.onNativeEvent);
    this.native.addEventListener("wheel", this.onNativeEvent, {
      passive: true,
      capture: true
    });
  };

  BrowserCanvas.prototype.getWorldPosition = function getWorldPosition(clientX, clientY) {
    var bounding = this.native.getBoundingClientRect();
    var canvasX = clientX - bounding.left;
    var canvasY = clientY - bounding.top;
    var worldX = canvasX * this.width / bounding.width;
    var worldY = canvasY * this.height / bounding.height;
    return new Point(worldX, worldY);
  };

  Object.defineProperties(BrowserCanvas.prototype, prototypeAccessors);
  return BrowserCanvas;
}(Canvas);

var BrowserCanvasState = /*@__PURE__*/function () {
  function BrowserCanvasState(ctx, userTransform) {
    this._committedTransformId = 0;
    this._saves = [];
    this._defaultValue = {
      textBaseline: "alphabetic",
      fillStyle: "#000000",
      strokeStyle: "#000000",
      font: "",
      filter: "none",
      globalAlpha: 1,
      globalCompositeOperation: "",
      lineWidth: 1,
      lineCap: "butt",
      lineJoin: "miter",
      lineDash: [],
      lineDashOffset: 0,
      miterLimit: 10,
      transform: Matrix.Identity,
      userTransform: Matrix.Identity
    };
    this._changes = /* @__PURE__ */new Set();
    this.textBaseline = ctx.textBaseline;
    this.fillStyle = ctx.fillStyle;
    this.strokeStyle = ctx.strokeStyle;
    this.font = ctx.font;
    this.filter = ctx.filter;
    this.globalAlpha = ctx.globalAlpha;
    this.globalCompositeOperation = ctx.globalCompositeOperation;
    this.lineWidth = ctx.lineWidth;
    this.lineCap = ctx.lineCap;
    this.lineJoin = ctx.lineJoin;
    this.lineDash = ctx.getLineDash();
    this.lineDashOffset = ctx.lineDashOffset;
    this.miterLimit = ctx.miterLimit;
    var m = "getTransform" in ctx ? ctx.getTransform() : identityDOMMatrix;
    this.transform = new Matrix(m.a, m.b, m.c, m.d, m.e, m.f);
    this.userTransform = userTransform;
    Object.assign(this._defaultValue, this);
  }

  BrowserCanvasState.prototype.set = function set(key, value) {
    if (this[key] === value) {
      return;
    }

    this[key] = value;

    this._changes.add(key);
  };

  BrowserCanvasState.prototype.setTransform = function setTransform(a, b, c, d, tx, ty) {
    var ref = this;
    var transform = ref.transform;
    var equals = a === transform.a && b === transform.b && c === transform.c && d === transform.d && tx === transform.tx && ty === transform.ty;

    if (!equals) {
      transform.set(a, b, c, d, tx, ty);
    }
  };

  BrowserCanvasState.prototype.commit = function commit(ctx) {
    var this$1$1 = this;

    if (this._committedTransformId !== this.transform._id) {
      this._committedTransformId = this.transform._id;
      var ref = this.transform;
      var a = ref.a;
      var b = ref.b;
      var c = ref.c;
      var d = ref.d;
      var tx = ref.tx;
      var ty = ref.ty;
      ctx.setTransform(a, b, c, d, tx, ty);
    }

    if (this._changes.size > 0) {
      this._changes.forEach(function (key) {
        if (key === "lineDash") {
          ctx.setLineDash(this$1$1.lineDash);
        } else {
          ctx[key] = this$1$1[key];
        }
      });

      this._changes.clear();
    }
  };

  BrowserCanvasState.prototype.save = function save() {
    this._saves.push({
      textBaseline: this.textBaseline,
      fillStyle: this.fillStyle,
      strokeStyle: this.strokeStyle,
      font: this.font,
      filter: this.filter,
      globalAlpha: this.globalAlpha,
      globalCompositeOperation: this.globalCompositeOperation,
      lineWidth: this.lineWidth,
      lineCap: this.lineCap,
      lineJoin: this.lineJoin,
      lineDash: this.lineDash,
      lineDashOffset: this.lineDashOffset,
      miterLimit: this.miterLimit,
      transform: Matrix.fromMatrix(this.transform),
      userTransform: Matrix.fromMatrix(this.userTransform)
    });
  };

  BrowserCanvasState.prototype.restore = function restore() {
    if (this._saves.length === 0) {
      return;
    }

    var saved = this._saves.pop();

    this.transform.copyFrom(saved.transform);
    saved.transform = this.transform;
    this.userTransform.copyFrom(saved.userTransform);
    saved.userTransform = this.userTransform;
    Object.assign(this, saved);
    this.forceUpdate();
  };

  BrowserCanvasState.prototype.forceUpdate = function forceUpdate() {
    this._committedTransformId = 0;
    this._changes = /* @__PURE__ */new Set(["textBaseline", "fillStyle", "strokeStyle", "font", "filter", "globalAlpha", "globalCompositeOperation", "lineWidth", "lineCap", "lineJoin", "lineDash", "lineDashOffset", "miterLimit"]);
  };

  BrowserCanvasState.prototype.resetStates = function resetStates(ctx) {
    this._changes.clear();

    this._saves = [];
    Object.assign(this, this._defaultValue);

    if ("textBaseline" in ctx) {
      ctx.textBaseline = this._defaultValue.textBaseline;
    }

    if ("fillStyle" in ctx) {
      ctx.fillStyle = this._defaultValue.fillStyle;
    }

    if ("strokeStyle" in ctx) {
      ctx.strokeStyle = this._defaultValue.strokeStyle;
    }

    if ("font" in ctx) {
      ctx.font = this._defaultValue.font;
    }

    if ("filter" in ctx) {
      ctx.filter = this._defaultValue.filter;
    }

    if ("globalAlpha" in ctx) {
      ctx.globalAlpha = this._defaultValue.globalAlpha;
    }

    if ("globalCompositeOperation" in ctx) {
      ctx.globalCompositeOperation = this._defaultValue.globalCompositeOperation;
    }

    if ("lineWidth" in ctx) {
      ctx.lineWidth = this._defaultValue.lineWidth;
    }

    if ("lineCap" in ctx) {
      ctx.lineCap = this._defaultValue.lineCap;
    }

    if ("lineJoin" in ctx) {
      ctx.lineJoin = this._defaultValue.lineJoin;
    }

    if ("setLineDash" in ctx) {
      ctx.setLineDash(this._defaultValue.lineDash);
    }

    if ("lineDashOffset" in ctx) {
      ctx.lineDashOffset = this._defaultValue.lineDashOffset;
    }

    if ("miterLimit" in ctx) {
      ctx.miterLimit = this._defaultValue.miterLimit;
    }
  };

  return BrowserCanvasState;
}();

var tempPadding = /*#__PURE__*/new Padding();
var identityDOMMatrix = {
  a: 1,
  b: 0,
  c: 0,
  d: 1,
  e: 0,
  f: 0
};
var BUFFER_OPTIONS = {
  bindEvents: false
};


CanvasPoolSingleton.getInstance().createOptions = BUFFER_OPTIONS;

var BrowserLoader = /*@__PURE__*/function (Loader) {
  function BrowserLoader() {
    Loader.apply(this, arguments);
  }

  if (Loader) BrowserLoader.__proto__ = Loader;
  BrowserLoader.prototype = /*#__PURE__*/Object.create(Loader && Loader.prototype);
  BrowserLoader.prototype.constructor = BrowserLoader;

  BrowserLoader.getInstance = function getInstance() {
    if (!BrowserLoader.instance) {
      BrowserLoader.instance = new BrowserLoader();
    }

    return BrowserLoader.instance;
  };

  BrowserLoader.prototype.load = function load(specifier) {
    if (isImageSource(specifier)) {
      return this.loadImage(specifier);
    }

    throw new Error("not supported");
  };

  BrowserLoader.prototype.loadImage = function loadImage(urlOrImage, options) {
    if (options === void 0) options = {};

    if (typeof urlOrImage !== "string") {
      return new ImageResource(urlOrImage);
    }

    if (/\.svg/i.test(urlOrImage) && options.inlineSvg !== false) {
      return new ImageResource(this.loadInlineSvg(urlOrImage));
    }

    var crossOrigin = options.crossOrigin;
    if (crossOrigin === void 0) crossOrigin = null;
    var nativeCors = crossOrigin === true ? "anonymous" : crossOrigin === false ? void 0 : crossOrigin;
    return new ImageResource(this.loadNativeImage(urlOrImage, nativeCors));
  };

  BrowserLoader.prototype.loadInlineSvg = function loadInlineSvg(url) {
    var this$1$1 = this;
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open("GET", url, true);
      xhr.onerror = reject;

      xhr.onreadystatechange = function () {
        if (xhr.readyState !== 4) {
          return;
        }

        if (xhr.status !== 200) {
          reject(new RangeError("The image response with code " + xhr.status));
        }

        var div = document.createElement("div");
        div.innerHTML = DOMPurify.sanitize(xhr.responseText, {
          USE_PROFILES: {
            svg: true,
            svgFilters: true
          }
        });
        var svg = div.querySelector(":scope > svg");

        if (!svg) {
          return reject(new TypeError("The requested resource is not a valid SVG."));
        }

        if (!svg.hasAttribute("width") || !svg.hasAttribute("height")) {
          div.style.cssText = "position: absolute; width: 0; height: 0";
          document.body.appendChild(div);
          var ref = svg.getBBox();
          var width = ref.width;
          var height = ref.height;
          svg.setAttribute("width", String(width));
          svg.setAttribute("height", String(height));
          document.body.removeChild(div);
        }

        svg.setAttribute("preserveAspectRatio", "none");
        var encoded = encodeURIComponent(svg.outerHTML).replace(/'/g, "%27").replace(/"/g, "%22");
        resolve(this$1$1.loadNativeImage("data:image/svg+xml," + encoded));
      };

      xhr.send();
    });
  };

  BrowserLoader.prototype.loadNativeImage = function loadNativeImage(url, crossOrigin) {
    var image = new Image();
    image.src = url;

    if (crossOrigin !== void 0) {
      image.crossOrigin = crossOrigin;
    }

    return new Promise(function (resolve, reject) {
      image.onload = function () {
        return resolve(image);
      };

      image.onerror = reject;
    });
  };

  return BrowserLoader;
}(Loader);
function isImageSource(specifier) {
  if (typeof specifier === "string") {
    return true;
  }

  if (specifier instanceof HTMLImageElement) {
    return true;
  }

  if (specifier instanceof HTMLVideoElement) {
    return true;
  }

  if (specifier instanceof HTMLCanvasElement) {
    return true;
  }

  if (specifier instanceof ImageBitmap) {
    return true;
  }

  return false;
}

var BrowserTarget = /*@__PURE__*/function (Target) {
  function BrowserTarget() {
    Target.apply(this, arguments);
    this.factory = BrowserCanvas;
    this.loader = BrowserLoader.getInstance();
    this.interactionFactory = DomInteractionEvent;
  }

  if (Target) BrowserTarget.__proto__ = Target;
  BrowserTarget.prototype = /*#__PURE__*/Object.create(Target && Target.prototype);
  BrowserTarget.prototype.constructor = BrowserTarget;
  return BrowserTarget;
}(Target);

export { BrowserCanvas, BrowserGlBuffer, BrowserLoader, BrowserTarget, DomInteractionEvent, isImageSource, nativeToInteractionMap };
//# sourceMappingURL=alloy-renderer-browser.esm.js.map
